import { DIVORCE_JURISDICTIONS } from '@/lib/divorce';

export type PlaceSuggestion = {
  id: string;
  label: string;
  city: string;
  region: string;
};

const ISO_REGION_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AR', 'AS', 'AT', 'AU', 'AW',
  'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BM', 'BN', 'BO',
  'BR', 'BS', 'BT', 'BW', 'BY', 'BZ', 'CA', 'CD', 'CF', 'CG', 'CH', 'CI', 'CL',
  'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO',
  'DZ', 'EC', 'EE', 'EG', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FM', 'FR', 'GA', 'GB',
  'GD', 'GE', 'GH', 'GI', 'GM', 'GN', 'GQ', 'GR', 'GT', 'GU', 'GW', 'GY', 'HK',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IN', 'IQ', 'IR', 'IS', 'IT', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC',
  'MD', 'ME', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MR', 'MT', 'MU', 'MV',
  'MW', 'MX', 'MY', 'MZ', 'NA', 'NE', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NZ',
  'OM', 'PA', 'PE', 'PG', 'PH', 'PK', 'PL', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA',
  'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SI', 'SK', 'SL',
  'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SY', 'SZ', 'TC', 'TD', 'TG', 'TH',
  'TJ', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'US',
  'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WS', 'XK', 'YE', 'ZA',
  'ZM', 'ZW',
];

const displayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const COUNTRY_NAMES = ISO_REGION_CODES.map((code) => displayNames?.of(code) ?? code)
  .filter((name, index, all) => Boolean(name) && all.indexOf(name) === index)
  .sort((a, b) => a.localeCompare(b));

const STATE_NAMES = DIVORCE_JURISDICTIONS.map((j) => j.name);

const COUNTIES_BY_STATE: Partial<Record<string, string[]>> = {
  CA: [
    'Alameda',
    'Alpine',
    'Amador',
    'Butte',
    'Calaveras',
    'Colusa',
    'Contra Costa',
    'Del Norte',
    'El Dorado',
    'Fresno',
    'Glenn',
    'Humboldt',
    'Imperial',
    'Inyo',
    'Kern',
    'Kings',
    'Lake',
    'Lassen',
    'Los Angeles',
    'Madera',
    'Marin',
    'Mariposa',
    'Mendocino',
    'Merced',
    'Modoc',
    'Mono',
    'Monterey',
    'Napa',
    'Nevada',
    'Orange',
    'Placer',
    'Plumas',
    'Riverside',
    'Sacramento',
    'San Benito',
    'San Bernardino',
    'San Diego',
    'San Francisco',
    'San Joaquin',
    'San Luis Obispo',
    'San Mateo',
    'Santa Barbara',
    'Santa Clara',
    'Santa Cruz',
    'Shasta',
    'Sierra',
    'Siskiyou',
    'Solano',
    'Sonoma',
    'Stanislaus',
    'Sutter',
    'Tehama',
    'Trinity',
    'Tulare',
    'Tuolumne',
    'Ventura',
    'Yolo',
    'Yuba',
  ],
};

export const STATE_COUNTRY_OPTIONS = uniqueSorted([...STATE_NAMES, ...COUNTRY_NAMES]);

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

export function suggestStateCountry(query: string, _signal?: AbortSignal): PlaceSuggestion[] {
  const q = query.trim().toLowerCase();
  if (q.length < 1) return [];
  return STATE_COUNTRY_OPTIONS.filter((name) => name.toLowerCase().includes(q))
    .slice(0, 8)
    .map((name) => ({
      id: name,
      label: name,
      city: '',
      region: name,
    }));
}

export function suggestCounties(
  query: string,
  stateCode: string,
  _signal?: AbortSignal,
): PlaceSuggestion[] {
  const q = query.trim().toLowerCase().replace(/\s+county$/, '');
  const counties = COUNTIES_BY_STATE[stateCode.toUpperCase()] ?? [];
  return counties
    .filter((county) => !q || county.toLowerCase().includes(q))
    .slice(0, 10)
    .map((county) => ({
      id: `${stateCode.toUpperCase()}-${county}`,
      label: `${county} County`,
      city: county,
      region: stateCode.toUpperCase(),
    }));
}

type PhotonFeature = {
  properties?: {
    osm_id?: number;
    osm_type?: string;
    name?: string;
    city?: string;
    state?: string;
    country?: string;
    countrycode?: string;
    type?: string;
  };
};

function joinLabel(parts: Array<string | undefined>): string {
  return parts.filter((part, index, all) => part && all.indexOf(part) === index).join(', ');
}

export async function suggestCities(
  query: string,
  signal?: AbortSignal,
): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const url = new URL('https://photon.komoot.io/api/');
  url.searchParams.set('q', q);
  url.searchParams.set('limit', '6');
  url.searchParams.set('lang', 'en');

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) return [];
  const data = (await res.json()) as { features?: PhotonFeature[] };

  const seen = new Set<string>();
  const suggestions: PlaceSuggestion[] = [];
  for (const feature of data.features ?? []) {
    const props = feature.properties ?? {};
    const city = (props.name || props.city || '').trim();
    if (!city) continue;
    const region = (props.state || props.country || '').trim();
    const label = joinLabel([city, props.state, props.country]);
    const id = `${props.osm_type ?? ''}-${props.osm_id ?? label}`;
    if (seen.has(label)) continue;
    seen.add(label);
    suggestions.push({ id, label, city, region });
    if (suggestions.length >= 6) break;
  }
  return suggestions;
}
